import {
  useAssignMarksMutation,
  useAssignBulkMarksMutation,
  useUpdateMarksMutation,
  useUpdateBulkMarksMutation,
  useGetMarksByStudentQuery,
  useGetAllMarksQuery,
} from "../api/assignMarksApi";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { MarksResponseType } from "../types/marksResponse";
import { MarksPayloadType } from "../types/marksRequest";
import { StudentResponseType } from "../../../peoples/students/types/studentResponse";

// Import BulkUpdatePayload from assignMarksApi
import { assignMarksApi } from "../api/assignMarksApi";
type BulkUpdatePayload = Parameters<
  typeof assignMarksApi.endpoints.updateBulkMarks.initiate
>[0];

interface BulkMarksPayload {
  marks: MarksPayloadType[];
}

export const useMarks = (params?: {
  session_id?: string;
  course_id?: string;
  stage_id?: string;
  student_id?: string;
  page?: number;
  size?: number;
  search?: string;
  students?: StudentResponseType[];
}) => {
  // Use getMarksByStudent if student_id is provided, otherwise use getAllMarks
  const {
    data: studentMarksData,
    isLoading: studentMarksLoading,
    error: studentMarksError,
  } = useGetMarksByStudentQuery(
    { student_id: params?.student_id || "" },
    { skip: !params?.student_id || !!params?.students }
  );

  // Skip getAllMarks query if students with marks are provided
  const {
    data: allMarksData,
    isLoading: allMarksLoading,
    error: allMarksError,
  } = useGetAllMarksQuery(
    {
      page: params?.page || 1,
      size: params?.size || 10,
      session_id: params?.session_id,
      course_id: params?.course_id,
      stage_id: params?.stage_id,
    },
    {
      skip: !!params?.students || !!params?.student_id,
    }
  );

  const [assignMark, { isLoading: isAssigning }] = useAssignMarksMutation();
  const [assignBulkMarks, { isLoading: isAssigningBulk }] =
    useAssignBulkMarksMutation();
  const [updateMark, { isLoading: isUpdating }] = useUpdateMarksMutation();
  const [updateBulkMarks, { isLoading: isUpdatingBulk }] =
    useUpdateBulkMarksMutation();

  // Update pagination data - if students are provided, we don't need pagination from API
  const paginationData = params?.students
    ? {
        totalPages: 1,
        pageSize: params.students.length,
        totalItems: params.students.length,
        currentPage: 1,
      }
    : {
        totalPages: allMarksData?.data?.totalPages || 0,
        pageSize: allMarksData?.data?.pageSize || 10,
        totalItems: allMarksData?.data?.totalItems || 0,
        currentPage: allMarksData?.data?.currentPage || 1,
      };

  const getMarksState = () => {
    // If students with marks are provided, extract marks from them
    if (params?.students?.length) {
      const marksFromStudents: MarksResponseType[] = [];

      params.students.forEach((student) => {
        if (student.marks && student.marks.length) {
          // Filter marks based on the current course, stage and session
          const relevantMarks = student.marks
            .filter((mark) => {
              // Match by course_id and session_id
              const courseMatch =
                !params.course_id ||
                mark.course_id === parseInt(params.course_id as string);

              const sessionMatch =
                !params.session_id ||
                mark.session_id === parseInt(params.session_id as string);

              // For stage, try to match by ID first
              let stageMatch =
                !params.stage_id ||
                mark.stage_id === parseInt(params.stage_id as string);

              // If no match by ID and we have stage info, try matching by name
              if (
                !stageMatch &&
                params.stage_id &&
                mark.stage?.name &&
                student.marks
              ) {
                // Find all marks with matching stage names in the student array
                const stageMatchesByName = student.marks.filter(
                  (m) => m.stage?.id === parseInt(params.stage_id as string)
                );

                if (stageMatchesByName.length > 0) {
                  const stageName = stageMatchesByName[0].stage?.name;
                  stageMatch = mark.stage?.name === stageName;
                }
              }

              return courseMatch && sessionMatch && stageMatch;
            })
            .map((mark) => ({
              ...mark,
              max: mark.stage?.max_marks,
              min: mark.stage?.min_marks,
            })) as MarksResponseType[];

          marksFromStudents.push(...relevantMarks);
        }
      });

      return {
        marks: marksFromStudents,
        isLoading: false,
        error: null,
      };
    }

    // Otherwise fall back to the original implementation
    return {
      marks: params?.student_id
        ? studentMarksData?.data?.marks || []
        : allMarksData?.data?.items || [],
      isLoading: params?.student_id ? studentMarksLoading : allMarksLoading,
      error: params?.student_id
        ? studentMarksError && getApiErrorMessage(studentMarksError)
        : allMarksError && getApiErrorMessage(allMarksError),
    };
  };

  const getStudentFullName = (mark: MarksResponseType) =>
    mark.student?.name || "";

  const getStudentDetails = (mark: MarksResponseType) => ({
    fullName: getStudentFullName(mark),
    studentId: mark.student?.id,
    courseName: mark.course?.name,
    stageName: mark.stage?.name,
    sessionName: mark.session?.name,
  });

  const saveMarks = async (payload: MarksPayloadType) => {
    try {
      const response = await assignMark(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  const saveBulkMarks = async (payload: BulkMarksPayload) => {
    try {
      const response = await assignBulkMarks(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  const updateMarks = async (
    id: number,
    payload: Partial<MarksPayloadType>
  ) => {
    try {
      const response = await updateMark({ id, payload }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  const updateBulkMarksData = async (payload: BulkUpdatePayload) => {
    try {
      // Ensure the payload matches the expected type by the API
      const response = await updateBulkMarks(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  return {
    getMarksState,
    paginationData,
    getStudentFullName,
    getStudentDetails,
    saveMarks,
    saveBulkMarks,
    updateMarks,
    updateBulkMarksData,
    isAssigning,
    isAssigningBulk,
    isUpdating,
    isUpdatingBulk,
  };
};
